import React, { useState, useEffect, useCallback } from 'react';
import Navbar from '../../components/navbar/navbar'
import apinode from '../../services/apinode.js';
import {
  Container, Row, Col, Card, Table, Badge, Button,
  Form, InputGroup, Spinner, Modal, ListGroup
} from 'react-bootstrap';
import {
  FiRefreshCw, FiPlay, FiTrash2, FiSearch,
  FiCheckCircle, FiAlertCircle, FiClock, FiLoader,
  FiArrowUp, FiArrowDown, FiList, FiChevronLeft, FiChevronRight
} from 'react-icons/fi';

const STATUS_CONFIG = {
  pendente:   { variant: 'warning',   label: 'Pendente',   icon: <FiClock size={11}/> },
  executando: { variant: 'primary',   label: 'Executando', icon: <FiLoader size={11}/> },
  concluido:  { variant: 'success',   label: 'Concluído',  icon: <FiCheckCircle size={11}/> },
  erro:        { variant: 'danger',    label: 'Erro',        icon: <FiAlertCircle size={11}/> },
  descartado:  { variant: 'secondary', label: 'Descartado',  icon: <FiTrash2 size={11}/> },
};

const ACAO_CONFIG = {
  ativar: { variant: 'success', label: 'Ativar', icon: <FiArrowUp size={11}/> },
  pausar: { variant: 'danger',  label: 'Pausar', icon: <FiArrowDown size={11}/> },
};

const LOG_STATUS_CONFIG = {
  sucesso:      { variant: 'success', label: 'Sucesso' },
  erro:         { variant: 'danger',  label: 'Erro'    },
  sem_anuncios: { variant: 'warning', label: 'Sem anúncios' },
};

export default function TarefasBot({ conta = '514066249' }) {
  const executadoPor = localStorage.getItem('username') || 'sistema';

  const [tarefas, setTarefas]             = useState([]);
  const [loading, setLoading]             = useState(false);
  const [executandoIds, setExecutandoIds] = useState(new Set());
  const [selecionadas, setSelecionadas]   = useState(new Set());
  const [busca, setBusca]                 = useState('');
  const [filtroStatus, setFiltroStatus]   = useState('');
  const [filtroAcao, setFiltroAcao]       = useState('');

  // ── Paginação ────────────────────────────────────────────────
  const [pagina, setPagina]         = useState(1);
  const [porPagina, setPorPagina]   = useState(20);

  // Reset página ao mudar filtros
  useEffect(() => { setPagina(1); }, [filtroStatus, filtroAcao, busca]);

  // ── Modal confirmação ────────────────────────────────────────
  const [showConfirm, setShowConfirm]   = useState(false);
  const [confirmAcao, setConfirmAcao]   = useState(null);

  // ── Modal logs ───────────────────────────────────────────────
  const [showLogs, setShowLogs]         = useState(false);
  const [tarefaLogs, setTarefaLogs]     = useState(null); // tarefa selecionada
  const [logs, setLogs]                 = useState([]);
  const [loadingLogs, setLoadingLogs]   = useState(false);

  // ── Carrega tarefas ──────────────────────────────────────────
  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroStatus) params.status = filtroStatus;
      if (filtroAcao)   params.acao   = filtroAcao;
      if (busca)        params.busca  = busca;
      const { data } = await apinode.get(`/tarefas-bot/${conta}`, { params });
      setTarefas(data);
      setSelecionadas(new Set());
    } catch (e) {
      console.error('Erro ao carregar tarefas:', e);
    } finally {
      setLoading(false);
    }
  }, [filtroStatus, filtroAcao, busca, conta]);

  useEffect(() => { carregar(); }, [carregar]);

  // ── Abre modal de logs ───────────────────────────────────────
  const abrirLogs = async (tarefa) => {
    setTarefaLogs(tarefa);
    setLogs([]);
    setShowLogs(true);
    setLoadingLogs(true);
    try {
      const { data } = await apinode.get(`/tarefas-bot/${conta}/${tarefa.ID}/logs`);
      setLogs(data);
    } catch (e) {
      console.error('Erro ao buscar logs:', e);
    } finally {
      setLoadingLogs(false);
    }
  };

  // ── Executar uma tarefa ──────────────────────────────────────
  const executarUma = async (id) => {
    setExecutandoIds(prev => new Set([...prev, id]));
    try {
      await apinode.post(`/tarefas-bot/${conta}/${id}/executar`, { executadoPor });
      await carregar();
    } catch (e) {
      console.error('Erro ao executar tarefa:', e);
    } finally {
      setExecutandoIds(prev => { const s = new Set(prev); s.delete(id); return s; });
    }
  };

  // ── Executar selecionadas ────────────────────────────────────
  const executarSelecionadas = async () => {
    setShowConfirm(false);
    for (const id of selecionadas) {
      await executarUma(id);
    }
    setSelecionadas(new Set());
  };

  // ── Descartar selecionadas ───────────────────────────────────
  const descartarSelecionadas = async () => {
    setShowConfirm(false);
    try {
      await Promise.all([...selecionadas].map(id =>
        apinode.delete(`/tarefas-bot/${conta}/${id}`, { data: { executadoPor } })
      ));
      await carregar();
    } catch (e) {
      console.error('Erro ao descartar:', e);
    }
  };

  // ── Seleção ──────────────────────────────────────────────────
  const toggleItem = (id) => {
    setSelecionadas(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const toggleTodos = (checked) => {
    if (checked) {
      setSelecionadas(new Set(
        tarefas.filter(t => t.STATUS === 'pendente' || t.STATUS === 'erro').map(t => t.ID)
      ));
    } else {
      setSelecionadas(new Set());
    }
  };

  // ── Métricas ─────────────────────────────────────────────────
  const metricas = {
    total:     tarefas.length,
    pendente:  tarefas.filter(t => t.STATUS === 'pendente').length,
    concluido: tarefas.filter(t => t.STATUS === 'concluido').length,
    erro:      tarefas.filter(t => t.STATUS === 'erro').length,
  };

  // ── Paginação calculada ─────────────────────────────────────
  const totalPaginas  = Math.max(1, Math.ceil(tarefas.length / porPagina));
  const paginaAtual   = Math.min(pagina, totalPaginas);
  const tarefasPagina = tarefas.slice((paginaAtual - 1) * porPagina, paginaAtual * porPagina);

  const podeSelecionarTodos = tarefas.filter(
    t => t.STATUS === 'pendente' || t.STATUS === 'erro'
  ).length;

  return (
    <>
    <Navbar/>
 
    <Container fluid className="py-4 px-4">

      {/* ── Cabeçalho ── */}
      <Row className="align-items-center mb-3">
        <Col>
          <h5 className="mb-0 fw-semibold">📋 Tarefas de estoque</h5>
          <small className="text-muted">Registradas via WhatsApp pelo comercial</small>
        </Col>
        <Col xs="auto" className="d-flex gap-2">
          {selecionadas.size > 0 && (
            <>
              <Button variant="outline-danger" size="sm"
                onClick={() => { setConfirmAcao('descartar'); setShowConfirm(true); }}>
                <FiTrash2 size={14} className="me-1"/>
                Arquivar ({selecionadas.size})
              </Button>
              <Button variant="success" size="sm"
                onClick={() => { setConfirmAcao('executar'); setShowConfirm(true); }}>
                <FiPlay size={14} className="me-1"/>
                Executar ({selecionadas.size})
              </Button>
            </>
          )}
          <Button variant="outline-secondary" size="sm" onClick={carregar} disabled={loading}>
            <FiRefreshCw size={14} className={loading ? 'spin' : ''}/>
          </Button>
        </Col>
      </Row>

      {/* ── Métricas ── */}
      <Row className="g-2 mb-3">
        {[
          { label: 'Total',      valor: metricas.total,     cor: 'secondary' },
          { label: 'Pendentes',  valor: metricas.pendente,  cor: 'warning'   },
          { label: 'Concluídas', valor: metricas.concluido, cor: 'success'   },
          { label: 'Com erro',   valor: metricas.erro,      cor: 'danger'    },
        ].map(m => (
          <Col key={m.label} xs={6} md={3}>
            <Card className="border-0 shadow-sm text-center py-2">
              <div className={`fw-bold fs-4 text-${m.cor}`}>{m.valor}</div>
              <div className="text-muted" style={{ fontSize: 12 }}>{m.label}</div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* ── Filtros ── */}
      <Row className="g-2 mb-3">
        <Col md={5}>
          <InputGroup size="sm">
            <InputGroup.Text><FiSearch size={13}/></InputGroup.Text>
            <Form.Control placeholder="Buscar SKU ou remetente..."
              value={busca} onChange={e => setBusca(e.target.value)}/>
          </InputGroup>
        </Col>
        <Col md={3}>
          <Form.Select size="sm" value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}>
            <option value="">Todos os status</option>
            <option value="pendente">Pendente</option>
            <option value="executando">Executando</option>
            <option value="concluido">Concluído</option>
            <option value="erro">Erro</option>
            <option value="descartado">Descartado</option>
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Select size="sm" value={filtroAcao} onChange={e => setFiltroAcao(e.target.value)}>
            <option value="">Todas as ações</option>
            <option value="ativar">Ativar</option>
            <option value="pausar">Pausar</option>
          </Form.Select>
        </Col>
      </Row>

      {/* ── Tabela ── */}
      <Card className="shadow-sm border-0">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" size="sm"/>
              <div className="text-muted mt-2" style={{ fontSize: 13 }}>Carregando tarefas...</div>
            </div>
          ) : tarefas.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <div style={{ fontSize: 32 }}>📭</div>
              <div style={{ fontSize: 13 }}>Nenhuma tarefa encontrada</div>
            </div>
          ) : (
            <Table hover responsive className="mb-0 align-middle" style={{ fontSize: 13 }}>
              <thead className="table-light">
                <tr>
                  <th style={{ width: 36, paddingLeft: 16 }}>
                    <Form.Check onChange={e => toggleTodos(e.target.checked)}
                      checked={selecionadas.size > 0 && selecionadas.size === podeSelecionarTodos}
                      disabled={podeSelecionarTodos === 0}/>
                  </th>
                  <th style={{ width: 50 }} className="text-muted fw-normal">#</th>
                  <th style={{ width: 100 }}>SKU</th>
                  <th>Descrição</th>
                  <th style={{ width: 110 }}>Ação</th>
                  <th style={{ width: 55 }}>Qtd</th>
                  <th>Remetente</th>
                  <th style={{ width: 155 }}>Data / hora</th>
                  <th style={{ width: 130 }}>Status</th>
                  <th>Executado por</th>
                  <th style={{ width: 80 }}></th>
                </tr>
              </thead>
              <tbody>
                {tarefasPagina.map(t => {
                  const st      = STATUS_CONFIG[t.STATUS]  || STATUS_CONFIG.pendente;
                  const ac      = ACAO_CONFIG[t.ACAO]      || {};
                  const exe     = executandoIds.has(t.ID);
                  const podeSel = t.STATUS === 'pendente' || t.STATUS === 'erro';

                  // Formata número de telefone bruto
                  const remetente = t.REMETENTE && /^\d{10,}$/.test(t.REMETENTE)
                    ? t.REMETENTE.replace(/^55(\d{2})(\d{4,5})(\d{4})$/, '+55 ($1) $2-$3')
                    : (t.REMETENTE || '—');

                  return (
                    <tr key={t.ID} className={selecionadas.has(t.ID) ? 'table-active' : ''}>
                      <td style={{ paddingLeft: 16 }}>
                        <Form.Check checked={selecionadas.has(t.ID)}
                          onChange={() => toggleItem(t.ID)} disabled={!podeSel}/>
                      </td>
                      <td className="text-muted">{t.ID}</td>
                      <td>
                        <span className="fw-semibold" style={{ fontFamily: 'monospace', fontSize: 13, color: 'inherit' }}>
                          {t.SKU}
                        </span>
                      </td>
                      <td className="text-muted" style={{ fontSize: 12, maxWidth: 200,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={t.DESCRICAO || ''}>
                        {t.DESCRICAO || <span style={{ color: '#ccc' }}>—</span>}
                      </td>
                      <td>
                        <Badge bg={ac.variant}
                          className="d-inline-flex align-items-center gap-1"
                          style={{ fontSize: 12, padding: '5px 10px', borderRadius: 20 }}>
                          {ac.icon} {t.ACAO}
                        </Badge>
                      </td>
                      <td className="fw-semibold text-center">{t.QUANTIDADE}</td>
                      <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {remetente}
                      </td>
                      <td className="text-muted" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                        {t.DATA_HORA ? new Date(t.DATA_HORA).toLocaleString('pt-BR') : '—'}
                      </td>
                      <td>
                        <Badge bg={st.variant}
                          className="d-inline-flex align-items-center gap-1"
                          style={{ fontSize: 12, padding: '5px 10px', borderRadius: 20 }}>
                          {st.icon} {st.label}
                        </Badge>
                      </td>
                      <td className="text-muted" style={{ fontSize: 12,
                        maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {t.EXECUTADO_POR || '—'}
                      </td>
                      <td>
                        <div className="d-flex gap-1 justify-content-end pe-2">
                          <Button variant="outline-secondary" size="sm"
                            style={{ padding: '3px 9px', borderRadius: 6 }} title="Logs"
                            onClick={() => abrirLogs(t)}>
                            <FiList size={13}/>
                          </Button>
                          {podeSel && (
                            <Button variant="outline-success" size="sm"
                              style={{ padding: '3px 9px', borderRadius: 6 }} title="Executar"
                              onClick={() => executarUma(t.ID)} disabled={exe}>
                              {exe
                                ? <Spinner animation="border" size="sm" style={{ width: 13, height: 13 }}/>
                                : <FiPlay size={13}/>
                              }
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
      {/* ── Paginação ── */}
      {tarefas.length > 0 && (
        <div className="d-flex align-items-center justify-content-between mt-3 px-1 flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2" style={{ fontSize: 13 }}>
            <span className="text-muted">Exibir</span>
            <Form.Select size="sm" style={{ width: 75 }} value={porPagina}
              onChange={e => { setPorPagina(Number(e.target.value)); setPagina(1); }}>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Form.Select>
            <span className="text-muted">por página</span>
            <span className="text-muted ms-2">
              {((paginaAtual - 1) * porPagina) + 1}–{Math.min(paginaAtual * porPagina, tarefas.length)} de <b>{tarefas.length}</b>
            </span>
          </div>
          <div className="d-flex gap-1">
            <Button variant="outline-secondary" size="sm"
              disabled={paginaAtual === 1}
              onClick={() => setPagina(1)}
              style={{ padding: '3px 9px' }}>«</Button>
            <Button variant="outline-secondary" size="sm"
              disabled={paginaAtual === 1}
              onClick={() => setPagina(p => p - 1)}
              style={{ padding: '3px 9px' }}><FiChevronLeft size={13}/></Button>

            {Array.from({ length: totalPaginas }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPaginas || Math.abs(p - paginaAtual) <= 1)
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                acc.push(p);
                return acc;
              }, [])
              .map((p, i) => p === '...'
                ? <Button key={`e${i}`} variant="outline-secondary" size="sm" disabled style={{ padding: '3px 9px' }}>…</Button>
                : <Button key={p} size="sm"
                    variant={p === paginaAtual ? 'primary' : 'outline-secondary'}
                    onClick={() => setPagina(p)}
                    style={{ padding: '3px 9px', minWidth: 32 }}>{p}</Button>
              )
            }

            <Button variant="outline-secondary" size="sm"
              disabled={paginaAtual === totalPaginas}
              onClick={() => setPagina(p => p + 1)}
              style={{ padding: '3px 9px' }}><FiChevronRight size={13}/></Button>
            <Button variant="outline-secondary" size="sm"
              disabled={paginaAtual === totalPaginas}
              onClick={() => setPagina(totalPaginas)}
              style={{ padding: '3px 9px' }}>»</Button>
          </div>
        </div>
      )}

      {/* ── Modal confirmação ── */}
      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 15 }}>
            {confirmAcao === 'executar' ? '▶ Executar tarefas' : '🗑 Arquivar tarefas'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: 13 }}>
          {confirmAcao === 'executar'
            ? `Executar ${selecionadas.size} tarefa(s) no Mercado Livre agora?`
            : `Arquivar ${selecionadas.size} tarefa(s)? Elas ficarão com status 'descartado'.`}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => setShowConfirm(false)}>
            Cancelar
          </Button>
          <Button variant={confirmAcao === 'executar' ? 'success' : 'danger'} size="sm"
            onClick={confirmAcao === 'executar' ? executarSelecionadas : descartarSelecionadas}>
            Confirmar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Modal logs ── */}
      <Modal show={showLogs} onHide={() => setShowLogs(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 15 }}>
            📋 Logs — SKU <code>{tarefaLogs?.SKU}</code>
            {' '}
            <Badge bg={ACAO_CONFIG[tarefaLogs?.ACAO]?.variant} style={{ fontSize: 11 }}>
              {tarefaLogs?.ACAO}
            </Badge>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: 13, maxHeight: '65vh', overflowY: 'auto' }}>

          {/* Resumo da tarefa */}
          {tarefaLogs && (
            <div className="p-2 mb-3 rounded" style={{ background: 'var(--bs-light)', fontSize: 12 }}>
              <Row className="g-1">
                <Col xs={6} md={3}><span className="text-muted">ID:</span> <b>{tarefaLogs.ID}</b></Col>
                <Col xs={6} md={3}><span className="text-muted">Qtd:</span> <b>{tarefaLogs.QUANTIDADE}</b></Col>
                <Col xs={6} md={3}><span className="text-muted">Remetente:</span> <b>{tarefaLogs.REMETENTE}</b></Col>
                <Col xs={6} md={3}><span className="text-muted">Por:</span> <b>{tarefaLogs.EXECUTADO_POR || '—'}</b></Col>
                {tarefaLogs.DESCRICAO && (
                  <Col xs={12} className="mt-1">
                    <span className="text-muted">Produto:</span> <b>{tarefaLogs.DESCRICAO}</b>
                  </Col>
                )}
              </Row>
            </div>
          )}

          {loadingLogs ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" variant="primary"/>
              <div className="text-muted mt-2">Buscando logs...</div>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <div style={{ fontSize: 28 }}>📭</div>
              <div>Nenhum log encontrado para esta tarefa</div>
            </div>
          ) : (
            <ListGroup variant="flush">
              {logs.map((log, idx) => {
                const lsCfg = LOG_STATUS_CONFIG[log.STATUS] || { variant: 'secondary', label: log.STATUS };
                let detalhe = [];
                try { detalhe = JSON.parse(log.DETALHE || '[]'); } catch {}
                return (
                  <ListGroup.Item key={log.ID} className="px-0 py-3">
                    {/* Cabeçalho do log */}
                    <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                      <Badge bg={lsCfg.variant}>{lsCfg.label}</Badge>
                      <span className="text-muted" style={{ fontSize: 11 }}>
                        {log.INICIO ? new Date(log.INICIO).toLocaleString('pt-BR') : '—'}
                        {log.FIM ? ` → ${new Date(log.FIM).toLocaleString('pt-BR')}` : ''}
                      </span>
                      <span className="ms-auto text-muted" style={{ fontSize: 11 }}>
                        por <b>{log.EXECUTADO_POR || '—'}</b>
                      </span>
                    </div>

                    {/* Contadores */}
                    <div className="d-flex gap-3 mb-2" style={{ fontSize: 12 }}>
                      <span>🏷 <b>{log.TOTAL_MLB || 0}</b> MLB(s)</span>
                      <span className="text-success">✅ <b>{log.TOTAL_OK || 0}</b> ok</span>
                      <span className="text-danger">❌ <b>{log.TOTAL_ERRO || 0}</b> erro</span>
                    </div>

                    {/* Detalhe por conta */}
                    {detalhe.length > 0 && (
                      <div style={{ fontSize: 11, background: '#f8f9fa', borderRadius: 6, padding: '8px 10px' }}>
                        {detalhe.map((d, i) => (
                          <div key={i} className="d-flex gap-2 align-items-center py-1"
                            style={{ borderBottom: i < detalhe.length - 1 ? '1px solid #dee2e6' : 'none' }}>
                            <span>{d.status === 'ok' ? '✅' : '❌'}</span>
                            <span className="fw-semibold">{d.nome}</span>
                            {d.tipo === 'variacao'
                              ? <span className="text-muted">{d.mlbs || d.total} MLB(s) · variações</span>
                              : <span className="text-muted">{d.total} anúncio(s)</span>
                            }
                            {d.erro && <span className="text-danger ms-auto">{d.erro}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </ListGroup.Item>
                );
              })}
            </ListGroup>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => setShowLogs(false)}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </Container>

    </>
  );
}